import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { loadTenant } from "../../lib/tenant";

function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
        toast.type === "success" ? "bg-gray-900 text-white" :
        toast.type === "warning" ? "bg-amber-500 text-white" :
        "bg-red-600 text-white"
      }`}>
      {toast.message}
    </div>
  );
}

function ConfirmModal({ open, onConfirm, onCancel, hasProgress, count }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
        <h2 className="text-base font-semibold text-gray-900 mb-2">Confirm Unenrolment</h2>
        {hasProgress ? (
          <p className="text-sm text-red-600 mb-4">
            One or more selected courses have already been started. Unenrolling will permanently delete the learner's progress from SCORM Cloud. This cannot be undone.
          </p>
        ) : (
          <p className="text-sm text-gray-600 mb-4">
            Are you sure you want to unenrol this learner from {count} course{count !== 1 ? "s" : ""}? This cannot be undone.
          </p>
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
          >
            Yes, Unenrol
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ enrolment }) {
  if (enrolment.completed) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium whitespace-nowrap">Completed</span>;
  }
  if (enrolment.scormcloud_registration_id) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium whitespace-nowrap">In Progress</span>;
  }
  return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium whitespace-nowrap">Not Started</span>;
}

const TABS = [
  { key: "enrol", label: "Enrol" },
  { key: "unenrol", label: "Unenrol" },
  { key: "complete", label: "Mark Complete" },
];

export default function AssignmentRules() {
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState("enrol");

  // mode only applies to the Enrol tab
  const [mode, setMode] = useState("user"); // "user" | "role"

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [courseSearch, setCourseSearch] = useState("");

  // Enrol tab — By User
  const [selectedCourseIds, setSelectedCourseIds] = useState(new Set());

  // Enrol tab — By Role
  const [selectedRoles, setSelectedRoles] = useState(new Set());
  const [roleSearch, setRoleSearch] = useState("");

  // Unenrol + Mark Complete tabs
  const [userEnrolments, setUserEnrolments] = useState([]);
  const [enrolmentsLoading, setEnrolmentsLoading] = useState(false);
  const [selectedEnrolmentIds, setSelectedEnrolmentIds] = useState(new Set());

  // Confirm modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmHasProgress, setConfirmHasProgress] = useState(false);

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  function showToast(message, type = "success") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const t = await loadTenant();
      setTenant(t);
      if (!t?.id) return;
      const { data: profileData } = await supabase
        .from("profiles")
        .select("user_id, first_name, last_name, email, groups")
        .eq("tenant_id", t.id)
        .order("last_name", { ascending: true });
      setUsers(profileData || []);
      const { data: courseData } = await supabase
        .from("courses")
        .select("id, title")
        .eq("tenant_id", t.id)
        .eq("active", true)
        .order("title", { ascending: true });
      setCourses(courseData || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadUserEnrolments = useCallback(async (userId) => {
    if (!userId || !tenant?.id) return;
    setEnrolmentsLoading(true);
    setSelectedEnrolmentIds(new Set());
    try {
      const { data } = await supabase
        .from("enrolments")
        .select("id, course_id, completed, completed_at, scormcloud_registration_id")
        .eq("tenant_id", tenant.id)
        .eq("user_id", userId);
      setUserEnrolments(data || []);
    } finally {
      setEnrolmentsLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    if ((tab === "unenrol" || tab === "complete") && selectedUserId) {
      loadUserEnrolments(selectedUserId);
    }
  }, [tab, selectedUserId, loadUserEnrolments]);

  function handleTabChange(newTab) {
    setTab(newTab);
    setSelectedUserId(null);
    setUserSearch("");
    setCourseSearch("");
    setSelectedCourseIds(new Set());
    setSelectedEnrolmentIds(new Set());
    setUserEnrolments([]);
    setSelectedRoles(new Set());
    setRoleSearch("");
  }

  function handleUserSelect(userId) {
    setSelectedUserId(userId);
    setSelectedCourseIds(new Set());
    setSelectedEnrolmentIds(new Set());
    setCourseSearch("");
    setUserEnrolments([]);
  }

  function handleModeSwitch(newMode) {
    setMode(newMode);
    setSelectedUserId(null);
    setUserSearch("");
    setSelectedRoles(new Set());
    setRoleSearch("");
    setSelectedCourseIds(new Set());
    setCourseSearch("");
  }

  // Derive distinct roles from groups[0]
  const distinctRoles = React.useMemo(() => {
    const roleSet = new Set();
    users.forEach((u) => {
      let g = u.groups;
      if (typeof g === "string") {
        try { g = JSON.parse(g); } catch { g = null; }
      }
      if (Array.isArray(g) && g[0]) {
        roleSet.add(String(g[0]).trim());
      }
    });
    return Array.from(roleSet).sort();
  }, [users]);

  const filteredRoles = distinctRoles.filter((r) =>
    r.toLowerCase().includes(roleSearch.toLowerCase())
  );

  const usersInSelectedRoles = React.useMemo(() => {
    if (selectedRoles.size === 0) return [];
    return users.filter((u) => {
      let g = u.groups;
      if (typeof g === "string") {
        try { g = JSON.parse(g); } catch { g = null; }
      }
      if (Array.isArray(g) && g[0]) {
        return selectedRoles.has(String(g[0]).trim());
      }
      return false;
    });
  }, [users, selectedRoles]);

  // ── ENROL — By User ────────────────────────────────────────────────────────
  async function enrolUser(userId, courseId, courseTitle, accessToken) {
    const { data: existing } = await supabase
      .from("enrolments")
      .select("id")
      .eq("tenant_id", tenant.id)
      .eq("user_id", userId)
      .eq("course_id", courseId)
      .maybeSingle();
    if (existing) return { skipped: true };
    const { error } = await supabase
      .from("enrolments")
      .insert({ tenant_id: tenant.id, user_id: userId, course_id: courseId, completed: false });
    if (error) return { error };

    // Fire and forget — does not block or fail enrolment if email fails
    fetch("/api/scorm/enrolment-notify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, courseTitle, accessToken }),
    }).catch(() => {});

    return { enrolled: true };
  }

  async function handleEnrol() {
    if (!selectedUserId || selectedCourseIds.size === 0) return;
    setSaving(true);
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    try {
      const user = users.find((u) => u.user_id === selectedUserId);
      let enrolled = 0, skipped = 0, errors = 0;
      for (const courseId of selectedCourseIds) {
        const course = courses.find((c) => c.id === courseId);
        const result = await enrolUser(selectedUserId, courseId, course?.title || "", accessToken);
        if (result.enrolled) enrolled++;
        else if (result.skipped) skipped++;
        else errors++;
      }
      const type = errors > 0 ? "error" : skipped === selectedCourseIds.size ? "warning" : "success";
      const parts = [];
      if (enrolled > 0) parts.push(`${enrolled} enrolled`);
      if (skipped > 0) parts.push(`${skipped} already enrolled`);
      if (errors > 0) parts.push(`${errors} failed`);
      showToast(`${user?.first_name} ${user?.last_name}: ${parts.join(", ")}.`, type);
      setSelectedCourseIds(new Set());
    } finally {
      setSaving(false);
    }
  }

  // ── ENROL — By Role ────────────────────────────────────────────────────────
  async function handleEnrolByRole() {
    if (selectedRoles.size === 0 || selectedCourseIds.size === 0) return;
    setSaving(true);
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    try {
      const targetUsers = usersInSelectedRoles;
      let enrolled = 0, skipped = 0, errors = 0;
      for (const user of targetUsers) {
        for (const courseId of selectedCourseIds) {
          const course = courses.find((c) => c.id === courseId);
          const result = await enrolUser(user.user_id, courseId, course?.title || "", accessToken);
          if (result.enrolled) enrolled++;
          else if (result.skipped) skipped++;
          else errors++;
        }
      }
      const roleLabel = Array.from(selectedRoles).join(" + ");
      const type = errors > 0 ? "error" : skipped > 0 && enrolled === 0 ? "warning" : "success";
      const parts = [];
      if (enrolled > 0) parts.push(`${enrolled} enrolled`);
      if (skipped > 0) parts.push(`${skipped} already enrolled`);
      if (errors > 0) parts.push(`${errors} failed`);
      showToast(`${roleLabel} (${targetUsers.length} user${targetUsers.length !== 1 ? "s" : ""}): ${parts.join(", ")}.`, type);
      setSelectedCourseIds(new Set());
    } finally {
      setSaving(false);
    }
  }

  // ── UNENROL ────────────────────────────────────────────────────────────────
  function handleUnenrolClick() {
    if (selectedEnrolmentIds.size === 0) return;
    const anyHasProgress = [...selectedEnrolmentIds].some((id) => {
      const e = userEnrolments.find((x) => x.id === id);
      return !!e?.scormcloud_registration_id;
    });
    setConfirmHasProgress(anyHasProgress);
    setConfirmOpen(true);
  }

  async function handleUnenrolConfirm() {
    setConfirmOpen(false);
    setSaving(true);
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    let success = 0, errors = 0;
    try {
      for (const enrolmentId of selectedEnrolmentIds) {
        try {
          const res = await fetch("/api/scorm/unenrol", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ enrolmentId, accessToken }),
          });
          if (res.ok) success++;
          else errors++;
        } catch {
          errors++;
        }
      }
      const user = users.find((u) => u.user_id === selectedUserId);
      const parts = [];
      if (success > 0) parts.push(`${success} unenrolled`);
      if (errors > 0) parts.push(`${errors} failed`);
      showToast(`${user?.first_name} ${user?.last_name}: ${parts.join(", ")}.`, errors > 0 ? "error" : "success");
      await loadUserEnrolments(selectedUserId);
    } finally {
      setSaving(false);
    }
  }

  // ── MARK COMPLETE ──────────────────────────────────────────────────────────
  async function handleMarkComplete() {
    if (selectedEnrolmentIds.size === 0) return;
    setSaving(true);
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    let success = 0, errors = 0;
    try {
      for (const enrolmentId of selectedEnrolmentIds) {
        try {
          const res = await fetch("/api/scorm/mark-complete", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ enrolmentId, accessToken }),
          });
          if (res.ok) success++;
          else errors++;
        } catch {
          errors++;
        }
      }
      const user = users.find((u) => u.user_id === selectedUserId);
      const parts = [];
      if (success > 0) parts.push(`${success} marked complete`);
      if (errors > 0) parts.push(`${errors} failed`);
      showToast(`${user?.first_name} ${user?.last_name}: ${parts.join(", ")}.`, errors > 0 ? "error" : "success");
      await loadUserEnrolments(selectedUserId);
    } finally {
      setSaving(false);
    }
  }

  // ── Derived ────────────────────────────────────────────────────────────────
  const filteredUsers = users.filter((u) =>
    `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(userSearch.toLowerCase())
  );
  const filteredCourses = courses.filter((c) =>
    c.title.toLowerCase().includes(courseSearch.toLowerCase())
  );
  const selectedUser = users.find((u) => u.user_id === selectedUserId);

  const enrolmentsWithTitles = userEnrolments
    .map((e) => ({
      ...e,
      title: courses.find((c) => c.id === e.course_id)?.title || "Unknown Course",
    }))
    .filter((e) => e.title.toLowerCase().includes(courseSearch.toLowerCase()));

  const enrolByUserReady = mode === "user" && selectedUserId && selectedCourseIds.size > 0;
  const enrolByRoleReady = mode === "role" && selectedRoles.size > 0 && selectedCourseIds.size > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800" />
      </div>
    );
  }

  return (
    <>
      <Toast toast={toast} />
      <ConfirmModal
        open={confirmOpen}
        hasProgress={confirmHasProgress}
        count={selectedEnrolmentIds.size}
        onConfirm={handleUnenrolConfirm}
        onCancel={() => setConfirmOpen(false)}
      />

      <div className="p-6 space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Course Enrolment</h1>
          <p className="text-sm text-gray-500 mt-1">Manage learner enrolments across your courses.</p>
        </div>

        {/* Tab bar */}
        <div className="flex gap-2">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => handleTabChange(key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                tab === key ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Mode toggle — Enrol tab only */}
        {tab === "enrol" && (
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
            <button
              onClick={() => handleModeSwitch("user")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                mode === "user" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              By User
            </button>
            <button
              onClick={() => handleModeSwitch("role")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                mode === "role" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              By Role
            </button>
          </div>
        )}

        <div className="flex gap-4 h-[calc(100vh-260px)]">

          {/* LEFT panel */}
          <div className="w-72 flex-shrink-0 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">

            {/* By User */}
            {(mode === "user" || tab !== "enrol") && (
              <>
                <div className="p-4 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    Users ({users.length})
                  </p>
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-800"
                  />
                </div>
                <div className="overflow-y-auto flex-1">
                  {filteredUsers.length === 0 && (
                    <p className="text-center text-sm text-gray-400 py-8">No users found</p>
                  )}
                  {filteredUsers.map((u) => {
                    const isSelected = selectedUserId === u.user_id;
                    return (
                      <button
                        key={u.user_id}
                        onClick={() => handleUserSelect(u.user_id)}
                        className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors hover:bg-gray-50 ${
                          isSelected ? "bg-gray-900 hover:bg-gray-900" : ""
                        }`}
                      >
                        <p className={`text-sm font-medium truncate ${isSelected ? "text-white" : "text-gray-800"}`}>
                          {u.first_name} {u.last_name}
                        </p>
                        <p className={`text-xs truncate mt-0.5 ${isSelected ? "text-gray-300" : "text-gray-400"}`}>
                          {u.email}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* By Role */}
            {mode === "role" && tab === "enrol" && (
              <>
                <div className="p-4 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    Roles ({distinctRoles.length})
                  </p>
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={roleSearch}
                    onChange={(e) => setRoleSearch(e.target.value)}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-800"
                  />
                </div>
                <div className="overflow-y-auto flex-1">
                  {filteredRoles.length === 0 && (
                    <p className="text-center text-sm text-gray-400 py-8">No roles found</p>
                  )}
                  {filteredRoles.map((r) => {
                    const isSelected = selectedRoles.has(r);
                    const count = users.filter((u) => {
                      let g = u.groups;
                      if (typeof g === "string") { try { g = JSON.parse(g); } catch { g = null; } }
                      return Array.isArray(g) && String(g[0]).trim() === r;
                    }).length;
                    return (
                      <button
                        key={r}
                        onClick={() => {
                          setSelectedRoles((prev) => {
                            const next = new Set(prev);
                            if (next.has(r)) next.delete(r);
                            else next.add(r);
                            return next;
                          });
                          setSelectedCourseIds(new Set());
                          setCourseSearch("");
                        }}
                        className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors hover:bg-gray-50 ${
                          isSelected ? "bg-gray-900 hover:bg-gray-900" : ""
                        }`}
                      >
                        <p className={`text-sm font-medium truncate ${isSelected ? "text-white" : "text-gray-800"}`}>
                          {r}
                        </p>
                        <p className={`text-xs mt-0.5 ${isSelected ? "text-gray-300" : "text-gray-400"}`}>
                          {count} user{count !== 1 ? "s" : ""}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* RIGHT panel */}
          <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">

            {/* Empty state — By User */}
            {(mode === "user" || tab !== "enrol") && !selectedUserId && (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                <svg className="w-12 h-12 mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <p className="text-sm font-medium">Select a user to continue</p>
              </div>
            )}

            {/* Empty state — By Role */}
            {mode === "role" && tab === "enrol" && selectedRoles.size === 0 && (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                <svg className="w-12 h-12 mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-sm font-medium">Select one or more roles to continue</p>
              </div>
            )}

            {/* By User — content */}
            {((mode === "user" && selectedUserId) || (tab !== "enrol" && selectedUserId)) && (
              <>
                <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      {selectedUser?.first_name} {selectedUser?.last_name}
                    </h2>
                    <p className="text-xs text-gray-400 mt-0.5">{selectedUser?.email}</p>
                  </div>
                  {tab === "enrol" && (
                    <button
                      onClick={handleEnrol}
                      disabled={!enrolByUserReady || saving}
                      className="px-5 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 disabled:opacity-40 transition-colors"
                    >
                      {saving ? "Enrolling…" : selectedCourseIds.size > 0
                        ? `Enrol ${selectedCourseIds.size} Course${selectedCourseIds.size > 1 ? "s" : ""}`
                        : "Enrol"}
                    </button>
                  )}
                  {tab === "unenrol" && (
                    <button
                      onClick={handleUnenrolClick}
                      disabled={selectedEnrolmentIds.size === 0 || saving}
                      className="px-5 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-40 transition-colors"
                    >
                      {saving ? "Unenrolling…" : selectedEnrolmentIds.size > 0
                        ? `Unenrol ${selectedEnrolmentIds.size} Course${selectedEnrolmentIds.size > 1 ? "s" : ""}`
                        : "Unenrol"}
                    </button>
                  )}
                  {tab === "complete" && (
                    <button
                      onClick={handleMarkComplete}
                      disabled={selectedEnrolmentIds.size === 0 || saving}
                      className="px-5 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-40 transition-colors"
                    >
                      {saving ? "Saving…" : selectedEnrolmentIds.size > 0
                        ? `Mark ${selectedEnrolmentIds.size} Complete`
                        : "Mark Complete"}
                    </button>
                  )}
                </div>

                <div className="px-5 py-3 border-b border-gray-100">
                  <input
                    type="text"
                    placeholder="Search courses..."
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-800"
                  />
                </div>

                <div className="flex-1 overflow-y-auto p-5">
                  {tab === "enrol" && (
                    <>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        {selectedCourseIds.size > 0
                          ? `${selectedCourseIds.size} course${selectedCourseIds.size > 1 ? "s" : ""} selected`
                          : "Select courses to enrol"}
                      </p>
                      {filteredCourses.length === 0 && (
                        <p className="text-center text-sm text-gray-400 py-8">No courses match your search</p>
                      )}
                      <div className="space-y-2">
                        {filteredCourses.map((c) => {
                          const isSelected = selectedCourseIds.has(c.id);
                          return (
                            <button
                              key={c.id}
                              onClick={() => {
                                setSelectedCourseIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(c.id)) next.delete(c.id);
                                  else next.add(c.id);
                                  return next;
                                });
                              }}
                              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors group ${
                                isSelected
                                  ? "bg-gray-900 text-white"
                                  : "border border-gray-200 text-gray-700 hover:border-gray-900 hover:bg-gray-50"
                              }`}
                            >
                              <span className="truncate text-left">{c.title}</span>
                              {isSelected ? (
                                <svg className="w-4 h-4 ml-2 flex-shrink-0 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 ml-2 flex-shrink-0 text-gray-300 group-hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {(tab === "unenrol" || tab === "complete") && (
                    <>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        {enrolmentsLoading
                          ? "Loading…"
                          : selectedEnrolmentIds.size > 0
                          ? `${selectedEnrolmentIds.size} selected`
                          : `${enrolmentsWithTitles.length} enrolment${enrolmentsWithTitles.length !== 1 ? "s" : ""}`}
                      </p>
                      {enrolmentsLoading && (
                        <div className="flex justify-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-800" />
                        </div>
                      )}
                      {!enrolmentsLoading && enrolmentsWithTitles.length === 0 && (
                        <p className="text-center text-sm text-gray-400 py-8">No enrolments found</p>
                      )}
                      {!enrolmentsLoading && (
                        <div className="space-y-2">
                          {enrolmentsWithTitles.map((e) => {
                            const isSelected = selectedEnrolmentIds.has(e.id);
                            const isDisabled = tab === "complete" && e.completed;
                            if (isDisabled) {
                              return (
                                <div
                                  key={e.id}
                                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm border border-gray-100 text-gray-400 bg-gray-50 cursor-not-allowed"
                                >
                                  <span className="truncate text-left">{e.title}</span>
                                  <StatusBadge enrolment={e} />
                                </div>
                              );
                            }
                            return (
                              <button
                                key={e.id}
                                onClick={() => {
                                  setSelectedEnrolmentIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(e.id)) next.delete(e.id);
                                    else next.add(e.id);
                                    return next;
                                  });
                                }}
                                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors group ${
                                  isSelected
                                    ? tab === "unenrol"
                                      ? "bg-red-600 text-white"
                                      : "bg-green-600 text-white"
                                    : "border border-gray-200 text-gray-700 hover:border-gray-900 hover:bg-gray-50"
                                }`}
                              >
                                <span className="truncate text-left">{e.title}</span>
                                <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                  {!isSelected && <StatusBadge enrolment={e} />}
                                  {isSelected ? (
                                    <svg className="w-4 h-4 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4 text-gray-300 group-hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            {/* By Role — content */}
            {mode === "role" && tab === "enrol" && selectedRoles.size > 0 && (
              <>
                <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      {Array.from(selectedRoles).join(" + ")}
                    </h2>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {usersInSelectedRoles.length} user{usersInSelectedRoles.length !== 1 ? "s" : ""} in {selectedRoles.size} role{selectedRoles.size !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <button
                    onClick={handleEnrolByRole}
                    disabled={!enrolByRoleReady || saving}
                    className="px-5 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 disabled:opacity-40 transition-colors"
                  >
                    {saving ? "Enrolling…" : selectedCourseIds.size > 0
                      ? `Enrol ${selectedCourseIds.size} Course${selectedCourseIds.size > 1 ? "s" : ""}`
                      : "Enrol"}
                  </button>
                </div>

                <div className="px-5 py-3 border-b border-gray-100">
                  <input
                    type="text"
                    placeholder="Search courses..."
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-800"
                  />
                </div>

                <div className="flex-1 overflow-y-auto p-5">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    {selectedCourseIds.size > 0
                      ? `${selectedCourseIds.size} course${selectedCourseIds.size > 1 ? "s" : ""} selected`
                      : "Select courses to enrol"}
                  </p>
                  {filteredCourses.length === 0 && (
                    <p className="text-center text-sm text-gray-400 py-8">No courses match your search</p>
                  )}
                  <div className="space-y-2">
                    {filteredCourses.map((c) => {
                      const isSelected = selectedCourseIds.has(c.id);
                      return (
                        <button
                          key={c.id}
                          onClick={() => {
                            setSelectedCourseIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(c.id)) next.delete(c.id);
                              else next.add(c.id);
                              return next;
                            });
                          }}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors group ${
                            isSelected
                              ? "bg-gray-900 text-white"
                              : "border border-gray-200 text-gray-700 hover:border-gray-900 hover:bg-gray-50"
                          }`}
                        >
                          <span className="truncate text-left">{c.title}</span>
                          {isSelected ? (
                            <svg className="w-4 h-4 ml-2 flex-shrink-0 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 ml-2 flex-shrink-0 text-gray-300 group-hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
